import { Globe } from 'react-feather';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Locale } from '__generated__/graphql';
import { mergeUrlParams } from '../helpers';
import { supportedLocales } from '../../i18n';
import UI from '../UI';
import useLocale from '../useLocale';
import useOnClickOutside from '../useOnClickOutside';

interface LanguageSelectorProps {
  locales?: Locale[];
  onChangeLocale?: (locale: Locale) => void;
  brand?: string;
  blank?: boolean
  alignH?: 'left' | 'center' | 'right';
  alignV?: 'top' | 'bottom';
}

const LanguageSelector = ({
  locales = supportedLocales.current, onChangeLocale, brand = 'gray.200', blank = false, alignH = 'center',
  alignV = 'top',
}: LanguageSelectorProps) => {
  const { t } = useTranslation('common');
  const { locale: activeLocale } = useLocale();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdown = useRef();

  useOnClickOutside(dropdown, () => setDropdownOpen(false));

  const getTo = (locale: Locale) => mergeUrlParams({ locale });

  const handleClick = (locale: Locale) => {
    setDropdownOpen(false);
    onChangeLocale?.(locale);
  };

  return (
    <UI.DropdownOptionsContainer ref={dropdown}>
      <UI.Button
        onClick={() => setDropdownOpen((open) => !open)}
        sc={{
          brand, blank, muted: dropdownOpen, size: 'sm', px: 1,
        }}
        name={t(`locales_local.${activeLocale}`)}
      >
        <UI.Icon>
          <Globe />
        </UI.Icon>
        {' '}
        {t(`locales_local.${activeLocale}`)}
        {' '}
        <UI.Caret />
      </UI.Button>

      {dropdownOpen && (
        <UI.Dropdown ref={dropdown} sc={{ open: true, alignH, alignV }}>
          {locales.map((locale) => {
            const sharedProps = {
              sc: {
                strong: locale === activeLocale,
                color: locale === activeLocale ? 'gray.800' : 'gray.600',
              },
              onClick: () => handleClick(locale),
              name: t(`locales_local.${locale}`),
              children: t(`locales_local.${locale}`),
            };

            if (onChangeLocale) {
              return <UI.Button {...sharedProps} key={locale} />;
            }

            return <UI.LinkButton {...sharedProps} to={getTo(locale)} key={locale} />;
          })}
        </UI.Dropdown>
      )}
    </UI.DropdownOptionsContainer>
  );
};

export default LanguageSelector;
